import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button } from '@heroui/react';
import { EmptyState } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils';

type ProductTableEmptyStateProps = {
    /** A search or a filter narrowed the list to nothing (BOS-199). */
    isFiltered: boolean;
    /** Clears the search and every filter. */
    onClearFilters: () => void;
};

/**
 * The grid's body when the page holds no rows. A filtered list that matched
 * nothing says so and offers the way back; with nothing applied, the catalog
 * itself is empty.
 */
export const ProductTableEmptyState: React.FC<ProductTableEmptyStateProps> = ({
    isFiltered,
    onClearFilters,
}) => {
    const { t } = useTranslation('pages');

    return (
        <EmptyState {...getNodeTestIdProp('product-table-empty')}>
            <EmptyState.Header>
                <EmptyState.Media variant="icon">
                    <Icon aria-hidden icon="gravity-ui:box" />
                </EmptyState.Media>
                <EmptyState.Title
                    {...getNodeTestIdProp('product-table-empty-title')}
                >
                    {isFiltered
                        ? t(
                              'products.components.productTable.empty.filteredTitle',
                          )
                        : t('products.components.productTable.empty.title')}
                </EmptyState.Title>
                <EmptyState.Description
                    {...getNodeTestIdProp('product-table-empty-subtitle')}
                >
                    {isFiltered
                        ? t(
                              'products.components.productTable.empty.filteredSubtitle',
                          )
                        : t('products.components.productTable.empty.subtitle')}
                </EmptyState.Description>
            </EmptyState.Header>
            {isFiltered ? (
                <EmptyState.Content>
                    <Button
                        onPress={onClearFilters}
                        variant="secondary"
                        {...getNodeTestIdProp('product-table-empty-clear')}
                    >
                        {t(
                            'products.components.productTable.empty.clearFilters',
                        )}
                    </Button>
                </EmptyState.Content>
            ) : null}
        </EmptyState>
    );
};
